var GameState = require('./entities/GameState');
var GameViewModel = require('./ViewModels/GameViewModel');
var MoveDefinitions = require('./Moves/Moves');
var { MoveTypes, Phase } = require('./Moves/MoveTypes');
var { ActionCategories, GroupPhase } = require('./Moves/ActionCategories');
var CardConverter = require('./Utilities/Converters/CardConverter');
var { IsShownDisabled, MilitaryCardsOverLimit } = require('./Moves/Costs');
var Rules = require('./Rules');
var Authority = require('./Server/Authority');
var { RunProductionPhase } = require('./Moves/Production');
var { FireTriggers, TriggerEvents } = require('./Effects/Effects');
var { NextDecisionFor, DecisionSubject, MoveWorkerCost, MoveWorkerCostDetails, CardBuildCost, DecisionOrderProblem } = require('./Moves/Decisions');
var { PaymentTotal, MostPayable } = require('./Moves/Colonization');
var { TargetInfo, DefenseValue } = require('./Moves/Aggression');
var { WarTargets } = require('./Moves/War');
var { OfferTargets, TradeRoute } = require('./Moves/Pact');
var { Age } = require('./Constants/Constants');
var { GroupChanges } = require('./Utilities/Stats');
var LeaderNames = require('./Constants/Names/LeaderNames');
var { TriggerGains } = require('./Moves/TriggerPreview');
var { ActionBar, BoardCells, PreviewMove, Triggers, TechKey } = require('./ActionBar');

const clone = (data) => JSON.parse(JSON.stringify(data));

// The turn controller: a player plays their turn on their view of the game, with undo, and sends their moves to the
// authority (see Server/Authority.js) when they do something that cannot be undone.
//
// Undo model:
//   checkpointData  - the view at the start of the turn, or right after the last irreversible move
//   pendingMoves    - moves since the checkpoint; the current state is always checkpoint + pendingMoves
// Undo / reset / retract just change pendingMoves and replay from the checkpoint. A single earlier move can be
// undone only if every later move is still legal without it, so a move that later ones build on (taking a leader
// that was then elected, discovering a technology that was then built on, passing the political phase before
// taking actions) stays put until those later moves are undone.
//
// options.authority: where moves are committed and views come from. By default an Authority in this process on the
// given game data, seeing everything (one screen, and tests). The client passes one that talks to the server, with
// options.view (the view already fetched) and options.viewer (the player looking). Committing to a remote authority
// is asynchronous: submitMove then returns a promise for the result.
// options.keep: somewhere this turn's pending moves outlast the page (the client's browser storage), { load, save }:
// save({ version, moves, descriptions }) after every change (null when there are none), load() what was saved. They
// are picked up again only on the very view they were made on (the same version); never sent anywhere.
// Whether going from one state to the other drew on anything this player cannot see (a card drawn or dealt, a deck
// changed, a shuffle): only the authority can work that out
const RevealsHidden = (before, after) =>
    before.rngSeed !== after.rngSeed
    || ['militaryDeck', 'militaryDiscard', 'civilDeck', 'currentEvents', 'futureEvents', 'eventBox'].some(deck => before[deck].length !== after[deck].length)
    // (a hand growing: cards drawn; a card leaving one, e.g. a territory colonized from it, gives nothing away)
    || before.playerStates.some((player, index) => after.playerStates[index].militaryHand.length > player.militaryHand.length);

// Whether a step concerns only this player: no one else has a decision open before or after it, and no one else's
// civilization changes
const OnlyTheirs = (before, after, player) => {
    const others = (state) => JSON.stringify(state.playerStates.filter(p => p.playerNumber !== player).map(p => p.toData()));
    return !before.decisions.some(d => d.player !== player) && !after.decisions.some(d => d.player !== player) && others(before) === others(after);
};

// A card taken from the row for International Agreement, as a decision worked out here: { value: { cardId } }
const IsAgreementTake = (move) => move.type === MoveTypes.Decide && move.value?.cardId !== undefined;

class GameEngine {

    constructor(givenGameStateData, updateGameView = () => {}, options = {})
    {
        this.updateGameView = updateGameView;
        this.authority = options.authority ?? new Authority(givenGameStateData);
        this.viewer = options.viewer ?? null;
        this.pendingMoves = [];
        this.pendingDescriptions = [];
        this.lastError = null;
        // Waiting for the authority to answer a commit
        this.waiting = false;
        // (pools of extra resources, e.g. Patriotism's for military units, always pay what they can first: see Costs WithPools)
        this.useTradeRoute = false;
        this.keep = options.keep ?? null;
        const kept = this.keep?.load() ?? null;
        this.loadView(options.view ?? this.authority.ViewFor(this.viewer));
        this.restore(kept);
    }

    // The pending moves kept from before the page was reloaded, when the game has not moved on since (else dropped)
    restore = (kept) => {
        if (!kept || kept.version !== this.view.version || !(kept.moves?.length > 0))
        {
            this.save();
            return;
        }
        try {
            this.gameState = GameEngine.Replay(this.checkpointData, kept.moves);
            this.pendingMoves = kept.moves;
            this.pendingDescriptions = kept.descriptions ?? kept.moves.map(() => '');
        } catch (e) {
            // (no longer legal: e.g. the rules changed since)
        }
        this.notify();
    };

    save = () => this.keep?.save(this.pendingMoves.length > 0
        ? { version: this.view.version, moves: this.pendingMoves, descriptions: this.pendingDescriptions } : null);

    // Starts over from a view from the authority: its state is the new checkpoint, with nothing pending
    loadView = (view) => {
        this.view = view;
        this.checkpointData = JSON.parse(JSON.stringify(view.state));
        this.pendingMoves = [];
        this.pendingDescriptions = [];
        this.gameState = new GameState(JSON.parse(JSON.stringify(this.checkpointData)));
        this.notify();
    };

    // Catches up with a newer view (e.g. another player decided something) keeping this turn's pending moves on top.
    // If they no longer fit (the decision changed this player's board), the turn starts over from the new view.
    rebase = (view) => {
        const moves = this.pendingMoves;
        const descriptions = this.pendingDescriptions;
        this.loadView(view);
        if (moves.length === 0)
        {
            return;
        }
        try {
            this.gameState = GameEngine.Replay(this.checkpointData, moves);
            this.pendingMoves = moves;
            this.pendingDescriptions = descriptions;
            this.notify();
        } catch (e) {
            this.fail("Another player's decision changed the game, so your turn so far was undone");
        }
    };

    // Finalized moves, oldest first: { player, round, description }
    get journal()
    {
        return this.view.journal;
    }

    // Every move committed so far (only known when the authority is in this process)
    get committedMoves()
    {
        return this.authority.committedMoves ?? [];
    }

    // Whether the player looking is the one whose turn it is (always, when looking at everything)
    IsViewersTurn = () => this.viewer === null || this.viewer === this.gameState.currentPlayer;

    GetCurrentViewModel()
    {
        return this.gameState === null ? null
        : new GameViewModel(this.gameState, this.GetHistory());
    }

    GetHistory = () => ({
        ...this.GetRetractable(),
        viewer: this.viewer,
        isViewersTurn: this.IsViewersTurn(),
        // Seeing the game as it was when a decision was asked (see Authority.ViewFor)
        frozen: this.view.frozen === true,
        waiting: this.waiting,
        militaryDeckCandidates: this.view.state.militaryDeckCandidates ?? null,
        decisionRow: this.GetDecisionRow(),
        boardSelection: this.GetBoardSelection(),
        canUndo: this.pendingMoves.length > 0,
        resets: this.GetResets(),
        useRestricted: !this.declinedRestricted,
        useTradeRoute: this.useTradeRoute,
        tradeRoute: TradeRoute(this.gameState.GetCurrentPlayerState()),
        lastError: this.lastError,
        takenFromRow: this.GetTakenFromRow(),
        cardRowBlocked: this.GetCardRowBlocked(),
        actionOptions: this.GetActionOptions(),
        actionBar: this.GetActionBar(),
        journal: this.journal,
        // (once the game is over: who prepared which events)
        prepared: this.view.prepared ?? null,
        sinceLastTurn: this.GetSinceLastTurn(),
        endTurnPreview: this.GetEndTurnPreview(),
    });

    // This turn's moves, each marked with whether it can be undone on its own, and the card row cards taken this turn
    // that can still be put back
    GetRetractable = () => {
        const canRetract = this.pendingMoves.map((_, index) => {
            if (index === this.pendingMoves.length - 1)
            {
                return true;
            }
            try {
                GameEngine.Replay(this.checkpointData, this.pendingMoves.filter((_, i) => i !== index));
                return true;
            } catch (e) {
                return false;
            }
        });
        return {
            pendingMoves: this.pendingDescriptions.map((description, index) =>
                ({ index, description, move: this.pendingMoves[index], canUndo: canRetract[index] })),
            retractableCardIds: [
                ...this.pendingMoves.filter((move, index) => move.type === MoveTypes.TakeCard && canRetract[index]).map(move => move.cardId),
                // Cards taken for International Agreement can always be put back (the rest are taken again after it)
                ...this.pendingMoves.filter(IsAgreementTake).map(move => move.value.cardId),
            ],
        };
    };

    // Every category for the current phase with its legal moves (each option's move is ready to submit)
    // (worked out once for each game state and pool choice: the panel, the action bar and the board all ask)
    GetActionOptions = () => {
        const cached = this.actionOptionsCache;
        if (cached?.state !== this.gameState || cached.trade !== this.useTradeRoute || cached.declined !== this.declinedRestricted)
        {
            this.actionOptionsCache = { state: this.gameState, trade: this.useTradeRoute, declined: this.declinedRestricted,
                value: this.WorkOutActionOptions() };
        }
        return this.actionOptionsCache.value;
    };

    WorkOutActionOptions = () => {
        const player = this.gameState.GetCurrentPlayerState();
        // The costs listed follow the player's pool choices
        player.poolChoice = { decline: false, trade: this.useTradeRoute };
        player.restrictedDeclined = !!this.declinedRestricted;
        // Legal options as-is; ones blocked only by too few civil/military actions come back disabled with the reason
        const legal = (option) => {
            const move = this.withPoolChoice({ ...option.move, player: player.playerNumber });
            const error = GameEngine.Validate(this.gameState, move);
            if (error === null)
            {
                // (what the cards that react to it would do, e.g. Maria Theresa on Increase Population)
                const gains = TriggerGains(this.gameState, move);
                return { ...option, move, ...(gains.length ? { gains } : {}) };
            }
            return IsShownDisabled(error) ? { ...option, move, disabled: true, reason: error } : null;
        };
        const categories = ActionCategories
            .filter(category => GroupPhase[category.group] === this.gameState.phase)
            .map(category => ({
                id: category.id,
                title: category.title,
                label: category.label,
                group: category.group,
                implemented: category.generate !== undefined,
                onBoard: category.onBoard === true,
                single: category.single === true,
                prompt: category.prompt,
                ui: category.ui,
                neutral: category.neutral === true,
                options: (category.generate?.(this.gameState, player) ?? []).map(legal).filter(option => option),
            }));
        const endPhaseMove = this.gameState.phase === Phase.Political
            ? { label: "Pass political phase", move: { type: MoveTypes.PassPoliticalPhase, player: player.playerNumber } }
            : { label: "End turn", move: { type: MoveTypes.EndTurn, player: player.playerNumber,
                // (Saladin: his choice as it stands, else the strength; the end-of-turn dialog lets the player change it)
                ...(player.leader?.name === LeaderNames.Saladin ? { saladin: player.CardState(LeaderNames.Saladin).choice ?? 'strength' } : {}) } };
        // Over the military hand limit, ending the turn first needs a discard: offer it with the number to discard
        // (but not while the turn waits on a decision, e.g. another player's bid for a territory)
        const discard = this.gameState.phase === Phase.Action ? MilitaryCardsOverLimit(player) : 0;
        const endPhase = discard > 0 && !Rules.WaitingOn(this.gameState, player.playerNumber) ? { ...endPhaseMove, discard } : legal(endPhaseMove);
        return { categories, endPhase, decisions: this.GetDecisions(), waitingOn: this.GetWaitingOn() };
    };

    // The decisions the player looking has to make (looking at everything: everyone's), oldest first, each with its
    // answers as moves ready to submit. Only a player's oldest decision can be answered.
    GetDecisions = () => this.gameState.playerStates
        .filter(player => this.viewer === null || player.playerNumber === this.viewer)
        .map(player => NextDecisionFor(this.gameState, player.playerNumber))
        .filter(decision => decision)
        .map(decision => ({
            ...decision,
            playerName: this.gameState.playerStates[decision.player].name,
            // Waiting its turn behind an earlier blocking decision: why, or null
            waitingFor: DecisionOrderProblem(this.gameState, decision),
            options: (decision.options ?? []).map(option => ({
                ...option,
                move: { type: MoveTypes.Decide, player: decision.player, decisionId: decision.id, value: option.value },
            })),
        }));

    // Whether an answer to a decision would be accepted now, and what it would cost where that applies (for decisions
    // whose answers are checked rather than listed, e.g. Freedom of Movement): { ok, error?, cost?, move }
    TryDecision = (decisionId, value) => {
        const decision = this.gameState.decisions.find(d => d.id === decisionId);
        if (!decision)
        {
            return { ok: false, error: "No such decision" };
        }
        const move = { type: MoveTypes.Decide, player: decision.player, decisionId, value };
        const error = GameEngine.Validate(this.gameState, move);
        const player = this.gameState.playerStates[decision.player];
        const cost = decision.type === 'moveWorker' && value ? MoveWorkerCost(player, value) : undefined;
        // (and how it comes about, when both cards exist)
        const costDetails = decision.type === 'moveWorker' && value && player.GetTechnology(value.from) && player.GetTechnology(value.to)
            ? MoveWorkerCostDetails(player, value) : undefined;
        // Paying for a colony: what the payment is worth so far; bidding: the most this player could pay
        const total = decision.type === 'colonyPay' && value ? PaymentTotal(player, value) : undefined;
        const most = decision.type === 'colonyBid' ? MostPayable(player) : undefined;
        // Defending: what the cards chosen are worth
        const defense = decision.type === 'defend' && value ? DefenseValue(player, value) : undefined;
        return { ok: error === null, error, cost, costDetails, total, most, defense, move };
    };

    // Freedom of Movement, picked on the player's board: the open decision to move a worker (one the player looking can
    // make now), the two slots (where from, where to), which one board clicks fill (active), and how each card on the
    // board can be clicked: { decisionId, player, from, to, active, tried (the move, once both are picked), costs (of
    // each picked card), cells: { name: { selectable, reason?, mark? ('source' | 'target') } } }
    // Cards picked on a board for a decision (ui 'boardPick', e.g. what Border Conflict or a Raid destroys) are the same,
    // with up to context.pickCount slots (1 when not given): { decisionId, player (whose board: the decision's target,
    // else its player), pick: true, picked: [names], count, label, matched (the answer the cards picked make, if they
    // make one), tried, cells }. A card can be clicked while it leads to an answer (the same card twice when an answer
    // has it twice), or to take it back.
    // An action picked on the board (Destroy: StartBoardAction) is the same, with one slot: { action: its category,
    // label, picked, matched (its option), tried, cells } on the current player's board
    GetBoardSelection = () => {
        // A trigger of the action bar switched on: its targets on the current player's board
        if (this.boardSelection?.trigger)
        {
            const bar = this.GetActionBar();
            const player = this.gameState.GetCurrentPlayerState();
            return bar?.active ? { trigger: bar.active, player: player.playerNumber, cells: BoardCells(bar, this.gameState, player) } : null;
        }
        if (this.boardSelection?.action)
        {
            const player = this.gameState.GetCurrentPlayerState();
            const category = this.GetActionOptions().categories.find(c => c.id === this.boardSelection.action);
            if (!category)
            {
                return null;
            }
            const picked = this.boardSelection.picked ?? [];
            const option = category.options.find(o => o.move.techName === picked[0]);
            const cells = {};
            player.technologies.filter(tech => tech.workerCost !== null && tech.workerCost !== undefined).forEach(tech => {
                const offered = category.options.find(o => o.move.techName === tech.name);
                cells[tech.name] = offered && !offered.disabled
                    ? { selectable: true, mark: picked[0] === tech.name ? 'source' : undefined }
                    : { selectable: false, reason: offered?.reason ?? `Cannot be picked to ${category.prompt.toLowerCase()}` };
            });
            return { action: category.id, player: player.playerNumber, pick: true, picked, count: 1, label: category.label,
                matched: option ? { label: option.label, spends: option.spends } : null, tried: option ? { ok: !option.disabled, move: option.move } : null, cells };
        }
        const pick = this.GetDecisions().find(d => d.ui === 'boardPick' && !d.waitingFor);
        if (pick)
        {
            const picked = this.boardSelection?.decisionId === pick.id ? this.boardSelection.picked : [];
            const count = pick.context?.pickCount ?? 1;
            const board = this.gameState.playerStates[pick.context?.target ?? pick.player];
            const names = (value) => [].concat(value).slice().sort();
            // Whether these cards are all in an answer (as many times as there)
            const within = (cards, value) => {
                const left = names(value);
                return cards.every(card => { const at = left.indexOf(card); if (at < 0) return false; left.splice(at, 1); return true; });
            };
            const leadsOn = (cards) => pick.options.some(option => within(cards, option.value));
            const matched = picked.length > 0 ? pick.options.find(option => names(option.value).join('|') === names(picked).join('|')) : null;
            const cells = {};
            board.technologies.filter(tech => tech.workerCost !== null && tech.workerCost !== undefined).forEach(tech => {
                const isPicked = picked.includes(tech.name);
                // (with one slot, another card replaces the one picked)
                const adding = count === 1 ? [tech.name] : [...picked, tech.name];
                const canAdd = (count === 1 || picked.length < count) && leadsOn(adding);
                cells[tech.name] = canAdd || isPicked
                    ? { selectable: true, mark: isPicked ? 'source' : undefined }
                    : { selectable: false, reason: leadsOn([tech.name]) ? `At most ${count} for ${pick.event}` : `Cannot be picked for ${pick.event}` };
            });
            return { decisionId: pick.id, player: board.playerNumber, pick: true, picked, count, label: pick.context?.pickLabel ?? 'Pick',
                matched: matched ?? null, tried: matched ? this.TryDecision(pick.id, matched.value) : null, cells };
        }
        const decision = this.GetDecisions().find(d => d.ui === 'moveWorker' && !d.waitingFor);
        if (!decision)
        {
            return null;
        }
        const picked = this.boardSelection?.decisionId === decision.id ? this.boardSelection : { from: null, to: null, active: 'from' };
        // Where to can only be picked once where from is
        const active = picked.from ? picked.active : 'from';
        const player = this.gameState.playerStates[decision.player];
        const cells = {};
        player.technologies.filter(tech => tech.workerCost !== null && tech.workerCost !== undefined).forEach(tech => {
            const mark = tech.name === picked.from ? 'source' : tech.name === picked.to ? 'target' : undefined;
            if (active === 'from')
            {
                cells[tech.name] = tech.GetWorkerCount() > 0 ? { selectable: true, mark } : { selectable: false, reason: "No worker on it", mark };
            } else {
                // (Where from's card can be picked too: it becomes where to, and where from is blank again)
                const attempt = tech.name === picked.from ? { ok: true }
                    : this.TryDecision(decision.id, { from: picked.from, to: tech.name });
                cells[tech.name] = { selectable: attempt.ok, reason: attempt.ok ? undefined : attempt.error, mark };
            }
        });
        const tried = picked.from && picked.to ? this.TryDecision(decision.id, { from: picked.from, to: picked.to }) : null;
        // Each picked card's cost to build (the move costs the difference), shown under it
        const costs = { from: picked.from ? CardBuildCost(player, picked.from) : null, to: picked.to ? CardBuildCost(player, picked.to) : null };
        return { decisionId: decision.id, player: decision.player, from: picked.from, to: picked.to, active, tried, costs, cells };
    };

    // A card clicked on the board while moving a worker: it fills the active slot, and if it was in the other slot, that
    // one is cleared (a worker cannot move to the card it comes from). Then, if the other slot is blank, it becomes the
    // active one; otherwise the active slot stays, so later clicks change it again.
    ClickBoardCard = (name) => {
        const selection = this.GetBoardSelection();
        if (!selection?.cells[name]?.selectable)
        {
            return;
        }
        if (selection.trigger)
        {
            this.ClickTarget(TechKey(name));
            return;
        }
        // One card to pick: it is the one (clicked again, none)
        // Picking cards: with one slot, the card clicked is the one (clicked again, none); with more, it is added while it
        // leads to an answer, and otherwise (picked already) one of it is taken back
        if (selection.pick)
        {
            const { picked, count, decisionId } = selection;
            let next;
            if (selection.action)
            {
                this.boardSelection = { action: selection.action, picked: picked[0] === name ? [] : [name] };
                this.notify();
                return;
            }
            if (count === 1)
            {
                next = picked[0] === name ? [] : [name];
            } else {
                const adding = [...picked, name];
                const options = this.GetDecisions().find(d => d.id === decisionId).options;
                const leads = picked.length < count && options.some(option => {
                    const left = [].concat(option.value).slice();
                    return adding.every(card => { const at = left.indexOf(card); if (at < 0) return false; left.splice(at, 1); return true; });
                });
                next = leads ? adding : picked.filter((card, index) => index !== picked.lastIndexOf(name));
            }
            this.boardSelection = { decisionId, picked: next };
            this.notify();
            return;
        }
        const { decisionId, active } = selection;
        const from = active === 'from' ? name : selection.from === name ? null : selection.from;
        const to = active === 'to' ? name : selection.to === name ? null : selection.to;
        const other = active === 'from' ? 'to' : 'from';
        const otherBlank = other === 'to' ? to === null : from === null;
        this.boardSelection = { decisionId, from, to, active: otherBlank ? other : active };
        this.notify();
    };

// Picking on the board for an action (Destroy): start (the category's id), or stop (null)
    StartBoardAction = (categoryId) => {
        this.boardSelection = categoryId ? { action: categoryId, picked: [] } : null;
        this.notify();
    };

    // The action bar (see ActionBar.js), for the player looking: usable in their own action phase, while nothing is on
    // its way to the server. None at all once the game is over or for a player who has resigned (it could never be
    // used); no triggers in the first round, when only cards are taken from the card row.
    GetActionBar = () => {
        const looking = this.viewer ?? this.gameState.currentPlayer;
        if (this.gameState.finished || this.gameState.playerStates[looking]?.resigned)
        {
            this.boardSelection = this.boardSelection?.trigger ? null : this.boardSelection;
            return null;
        }
        const usable = [Phase.Action, Phase.Political].includes(this.gameState.phase) && this.IsViewersTurn() && !this.waiting && this.view.frozen !== true;
        const hidden = this.gameState.round === 1;
        // (a trigger left on goes off when the bar can't be used, or the phase is not its own, e.g. the turn reset to the
        // political phase, rather than coming back on by itself)
        const on = Triggers.find(t => t.id === this.boardSelection?.trigger);
        if ((!usable || hidden || (on && (on.phase ?? Phase.Action) !== this.gameState.phase)) && this.boardSelection?.trigger)
        {
            this.boardSelection = null;
        }
        const player = this.gameState.GetCurrentPlayerState();
        const bar = ActionBar({
            actionOptions: this.GetActionOptions(),
            picking: this.boardSelection?.trigger ? this.boardSelection : null,
            usable, hidden, phase: this.gameState.phase,
            // (a decision to make first, the player's own or one the turn waits on)
            gate: usable ? Rules.WaitingOn(this.gameState, player.playerNumber) : null,
            whyNot: this.WhyNot, costOf: this.CostOf, player, gameState: this.gameState,
        });
        // A card in hand that can be used more than one way, clicked: which, asked (handWays: { key, cardId, ways:
        // [{ trigger, label }] }); gone once it can't be
        const asking = this.boardSelection?.askHand;
        const entry = asking ? bar.hand?.[asking] : null;
        bar.handWays = entry?.ways ? { key: asking, cardId: entry.target?.options[0]?.move.cardId ?? null,
            ways: entry.ways.map(id => ({ trigger: id, label: Triggers.find(t => t.id === id).way })) } : null;
        if (asking && !bar.handWays)
        {
            this.boardSelection = null;
        }
        return bar;
    };

    // What using that card with these categories would cost (the first way to use it), though it can't be used now
    CostOf = (categoryIds, cardId) => {
        const player = this.gameState.GetCurrentPlayerState();
        for (const category of ActionCategories.filter(c => categoryIds.includes(c.id) && c.generate))
        {
            let candidates = [];
            try { candidates = category.generate(this.gameState, player) ?? []; } catch (e) { candidates = []; }
            const found = candidates.find(option => option.move.cardId === cardId && option.cost);
            if (found)
            {
                return found.cost;
            }
        }
        return null;
    };

    // Why none of these categories' moves can be made now (with that card, cardId): the rules' reason given most often
    // against the moves they could make (e.g. a tactic already changed this turn), or null when they make none. Worked
    // out once per game state.
    WhyNot = (categoryIds, cardId = null) => {
        if (this.whyNot?.state !== this.gameState)
        {
            this.whyNot = { state: this.gameState, found: {} };
        }
        const key = `${categoryIds.join('|')}#${cardId ?? ''}`;
        if (!(key in this.whyNot.found))
        {
            const player = this.gameState.GetCurrentPlayerState();
            const counts = {};
            ActionCategories.filter(category => categoryIds.includes(category.id) && category.generate).forEach(category => {
                let candidates = [];
                try { candidates = category.generate(this.gameState, player) ?? []; } catch (e) { candidates = []; }
                candidates.filter(option => cardId === null || option.move.cardId === cardId).forEach(option => {
                    const error = GameEngine.Validate(this.gameState, this.withPoolChoice({ ...option.move, player: player.playerNumber }));
                    if (error)
                    {
                        counts[error] = (counts[error] ?? 0) + 1;
                    }
                });
            });
            // (the most given; the first found of those given as often)
            this.whyNot.found[key] = Object.entries(counts).reduce((best, entry) => !best || entry[1] > best[1] ? entry : best, null)?.[0] ?? null;
        }
        return this.whyNot.found[key];
    };

    // A trigger clicked: switched on (or off, when it was on); one with nothing to pick acts at once
    StartTrigger = (id) => {
        const trigger = this.GetActionBar()?.triggers.find(t => t.id === id);
        if (!trigger?.enabled)
        {
            return;
        }
        if (trigger.immediate)
        {
            this.boardSelection = null;
            this.submitMove(trigger.option.move);
            return;
        }
        // (one with its one target given, Wonder: done at once, or its ways asked about, as a card in hand's are)
        if (trigger.direct && !trigger.active && trigger.target)
        {
            const options = trigger.target.options.filter(option => !option.disabled);
            if (options.length === 1)
            {
                this.boardSelection = null;
                this.submitMove(options[0].move);
                return;
            }
            this.boardSelection = { trigger: id, choosing: Triggers.find(t => t.id === id).key(options[0].move), fromHand: true };
            this.notify();
            return;
        }
        this.boardSelection = trigger.active ? null : { trigger: id };
        this.notify();
    };

    // The wonder being built clicked at one of its stages (static buttons): built up to it, as many stages as that is
    BuildWonderStages = (stages) => {
        const found = this.GetActionBar()?.wonderStages?.find(option => option.stages === stages);
        if (!found)
        {
            return;
        }
        this.boardSelection = null;
        this.submitMove(found.move);
    };

    // A target clicked with a trigger on: done at once, and the trigger goes off. Upgrade's where from is remembered
    // (clicked again, forgotten) until where to is picked; a target with several ways to do it asks which (ChooseTarget).
    ClickTarget = (key) => {
        const bar = this.GetActionBar();
        const target = bar?.targets[key];
        if (!target?.selectable)
        {
            return;
        }
        const { from, ...picking } = this.boardSelection ?? {};
        const pick = (selection) => {
            this.boardSelection = selection;
            this.notify();
        };
        if (bar.steps && !bar.from)
        {
            pick({ trigger: bar.active, from: key.slice('tech:'.length) });
            return;
        }
        if (bar.steps && key === TechKey(bar.from))
        {
            pick({ trigger: bar.active });
            return;
        }
        const options = target.options.filter(option => !option.disabled);
        // An action card being played (see the engine's InnerTargetsOf): the card again puts it back down; a card to
        // upgrade from is where from; where from again builds on it, if the card allows that too, or takes it back
        if (bar.cardPick)
        {
            if (key === `card:${bar.cardPick.card}`)
            {
                pick(picking.fromHand ? null : { trigger: bar.active });
                return;
            }
            if (!bar.from && target.upgradeFrom)
            {
                pick({ ...picking, from: key.slice('tech:'.length) });
                return;
            }
            if (bar.from && key === TechKey(bar.from) && options.length === 0)
            {
                pick(picking);
                return;
            }
        }
        // An action card that comes with an action, clicked: what it is used for is then picked where it is
        if (Triggers.find(t => t.id === bar.active).inner && !bar.cardPick && options.length > 1 && options.every(option => option.move.inner))
        {
            pick({ ...picking, card: options[0].move.cardId });
            return;
        }
        // (and one always asked about, e.g. an event confirmed before it is prepared)
        const def = Triggers.find(t => t.id === bar.active);
        if (options.length > 1 || (def.ask && (def.askIf?.(options[0].move) ?? true)))
        {
            this.boardSelection = { ...this.boardSelection, choosing: key };
            this.notify();
            return;
        }
        this.boardSelection = null;
        this.submitMove(options[0].move);
    };

    // A card in hand clicked with no trigger on (static buttons): done as its trigger would do it, the trigger on only
    // while a choice between its ways is open (cancelled, it goes off: see ChooseTarget). One that can be used more than
    // one way (Confucius) asks which first; way: the trigger chosen.
    ClickHandCard = (key, way = null) => {
        const entry = this.GetActionBar()?.hand?.[key];
        if (!entry?.selectable)
        {
            return;
        }
        if (entry.ways && !entry.ways.includes(way))
        {
            this.boardSelection = { askHand: key };
            this.notify();
            return;
        }
        this.boardSelection = { trigger: way ?? entry.trigger, fromHand: true };
        this.ClickTarget(key);
    };

    // A card on the board clicked with no trigger on (static buttons): Upgrade switched on, with it as where from
    StartUpgradeFrom = (name) => {
        if (!this.GetActionBar()?.upgradable?.includes(name))
        {
            return;
        }
        this.boardSelection = { trigger: 'upgrade', from: name };
        this.notify();
    };

    // The way chosen for a target with several (an option of the bar's choice), or null to choose another target
    ChooseTarget = (option) => {
        if (!option)
        {
            // (a choice asked with nothing picked, as a card in hand or a direct trigger was clicked: the trigger goes off)
            const { choosing, ...rest } = this.boardSelection ?? {};
            this.boardSelection = rest.trigger && !rest.fromHand ? rest : null;
            this.notify();
            return;
        }
        this.boardSelection = null;
        this.submitMove(option.move);
    };

    // What picking a target would do, for the popup by the mouse (key: a target's, or 'trigger:<id>' for a trigger's own
    // button): { items: [{ label, cost, costNote, spends, changes }] } (one for each way to do it), { reason } when it
    // cannot be picked (or used), { note } for how to go on (Upgrade's where from, a trigger); null for no popup. Worked
    // out once per target and game.
    PreviewTarget = (key) => {
        if (this.previews?.state !== this.gameState || this.previews.selection !== this.boardSelection)
        {
            this.previews = { state: this.gameState, selection: this.boardSelection, found: {} };
        }
        if (!(key in this.previews.found))
        {
            this.previews.found[key] = this.WorkOutPreview(key);
        }
        return this.previews.found[key];
    };

    WorkOutPreview = (key) => {
        const bar = this.GetActionBar();
        if (!bar)
        {
            return null;
        }
        const preview = (option) => {
            const found = PreviewMove(this.gameState, option.move, option);
            return found && { label: option.label, ...found };
        };
        // A trigger itself: why it can't be used; what one that acts at once would do; otherwise how it is used
        if (key.startsWith('trigger:'))
        {
            const trigger = bar.triggers.find(t => t.id === key.slice('trigger:'.length));
            if (!trigger)
            {
                return null;
            }
            return !trigger.enabled ? { reason: trigger.reason }
                : trigger.immediate ? { items: [preview(trigger.option)].filter(Boolean) }
                : trigger.direct && trigger.target ? this.PreviewOptions(trigger.target, Triggers.find(t => t.id === trigger.id), preview)
                : trigger.active ? { note: 'Click again (or Esc) to stop' }
                : { note: `Then pick ${trigger.pick}` };
        }
        // (a card in hand with no trigger on: as its trigger would have it)
        const hand = !bar.active ? bar.hand?.[key] : null;
        if (hand)
        {
            return !hand.selectable ? (hand.reason ? { reason: hand.reason } : null)
                : this.PreviewOptions(hand.target, Triggers.find(t => t.id === hand.trigger), preview);
        }
        const target = bar.targets[key];
        if (!target)
        {
            // (a board card: why not, as the board says)
            const cell = key.startsWith('tech:') && this.GetBoardSelection()?.cells?.[key.slice('tech:'.length)];
            return cell?.reason ? { reason: cell.reason } : null;
        }
        if (!target.selectable)
        {
            return target.reason ? { reason: target.reason } : null;
        }
        // (an action card being played: the card itself, and building on where from)
        if (bar.cardPick && key === `card:${bar.cardPick.card}`)
        {
            return { note: 'Click again to put it back down' };
        }
        if (target.mark === 'source' && !target.options.some(option => !option.disabled))
        {
            return { note: 'Click again to pick another' };
        }
        if ((bar.steps && !bar.from) || (target.upgradeFrom && !bar.from))
        {
            return { note: bar.cardPick && target.options.length > 0 ? 'Then pick what to upgrade it to, or it again to build on it' : 'Then pick what to upgrade it to' };
        }
        // (a political card: its own hover, the full card, instead)
        const trigger = Triggers.find(t => t.id === bar.active);
        return trigger.ask ? null : this.PreviewOptions(target, trigger, preview);
    };

    // A target's ways to do it, each previewed, named as the choice between them names it (e.g. "2 stages")
    PreviewOptions = (target, trigger, preview) => {
        const put = target.options.length > 1 ? trigger.choose?.(this.gameState.GetCurrentPlayerState(), target.options) : null;
        return { items: target.options.filter(option => !option.disabled).slice(0, 6)
            .map(option => put ? { ...option, label: put.label(option) } : option).map(preview).filter(Boolean) };
    };

    // A slot clicked in the decision area: board clicks fill that one from now on
    SetBoardSlot = (slot) => {
        const selection = this.GetBoardSelection();
        if (!selection)
        {
            return;
        }
        this.boardSelection = { decisionId: selection.decisionId, from: selection.from, to: selection.to, active: slot };
        this.notify();
    };

    // While the player looking takes cards for a decision (International Agreement): the card row's cards they can take
    // that way ({ cardId: move }) and why the others cannot ({ cardId: reason }); otherwise null
    GetDecisionRow = () => {
        const decision = this.GetDecisions().find(d => d.ui === 'agreementTake');
        if (!decision)
        {
            return null;
        }
        const moves = {};
        const blocked = {};
        this.gameState.civilCardRow.filter(card => card).forEach(card => {
            const tried = this.TryDecision(decision.id, { cardId: card.id });
            if (tried.ok)
            {
                moves[card.id] = tried.move;
            } else {
                blocked[card.id] = tried.error;
            }
        });
        return { moves, blocked };
    };

    // For choosing an aggression's target: what the aggressor would face against each rival (see Aggression.TargetInfo)
    GetAggressionTargets = (cardId) => {
        const player = this.gameState.GetCurrentPlayerState();
        const card = player.militaryHand.find(c => c.id === cardId);
        return card ? TargetInfo(this.gameState, player, card) : [];
    };

    // For choosing a war's target: each rival's strength and the cost against them (see War.WarTargets)
    GetWarTargets = (cardId) => {
        const player = this.gameState.GetCurrentPlayerState();
        const card = player.militaryHand.find(c => c.id === cardId);
        return card ? WarTargets(this.gameState, player, card) : [];
    };

    // For offering a pact: each rival with the roles that can be chosen and what each side would get (see Pact.OfferTargets)
    GetPactTargets = (cardId) => {
        const player = this.gameState.GetCurrentPlayerState();
        const card = player.militaryHand.find(c => c.id === cardId);
        return card ? OfferTargets(this.gameState, player, card) : [];
    };

    // Other players' open decisions, as everyone may know them: who has not decided yet, on what, and whether the turn
    // waits for them
    GetWaitingOn = () => this.gameState.decisions
        .filter(decision => this.viewer === null || decision.player !== this.viewer)
        .map(decision => ({ player: this.gameState.playerStates[decision.player].name, event: decision.event, blocking: decision.blocking,
            // (as a card, where it can be: see DecisionSubject)
            subject: DecisionSubject(this.gameState, decision) }));

    // Cards taken from the card row this turn, by the slot they came from: { [slot]: card }.
    // The row only changes between turns, so the slot is where the card sat at the checkpoint.
    GetTakenFromRow = () => {
        const taken = {};
        this.pendingMoves
            .filter(move => move.type === MoveTypes.TakeCard || IsAgreementTake(move))
            .forEach(move => {
                const cardId = move.cardId ?? move.value.cardId;
                const slot = this.checkpointData.civilCardRow.findIndex(card => card?.id === cardId);
                if (slot !== -1)
                {
                    taken[slot] = CardConverter(this.checkpointData.civilCardRow[slot]);
                }
            });
        return taken;
    };

    // Card row cards the current player cannot take right now, with the reason: { [cardId]: reason }.
    // Only during the action phase, when taking cards is possible at all.
    GetCardRowBlocked = () => {
        const blocked = {};
        if (this.gameState.phase !== Phase.Action)
        {
            return blocked;
        }
        this.gameState.civilCardRow.forEach(card => {
            if (card === null)
            {
                return;
            }
            const error = GameEngine.Validate(this.gameState, { type: MoveTypes.TakeCard, player: this.gameState.currentPlayer, cardId: card.id });
            if (error !== null)
            {
                blocked[card.id] = error;
            }
        });
        return blocked;
    };

    // What the viewer has not seen yet since their last turn (worked out by the authority; see Authority.SinceLastTurn)
    // The recap from the authority, with what this turn's moves not yet sent report (e.g. a pact cancelled, a war
    // declared, Columbus colonizing: political actions that can still be undone), worked out here
    GetSinceLastTurn = () => {
        const sent = this.view.sinceLastTurn;
        if (!sent || this.pendingMoves.length === 0)
        {
            return sent;
        }
        let state = new GameState(clone(this.checkpointData));
        const names = state.playerStates.map(player => player.name);
        const pending = [];
        for (const move of this.pendingMoves)
        {
            try {
                state = Rules.Step(state, move).after;
            } catch (e) {
                break;
            }
            pending.push(...(state.reports ?? []).map(report => ({ ...report, groups: GroupChanges(report.changes ?? [], names) })));
        }
        return pending.length === 0 ? sent : { ...sent, events: [...sent.events, ...pending] };
    };

    // What ending the turn now would do, for the end-of-turn dialog: cards to discard first, and the production
    // phase (uprising, corruption, famine, what is scored and produced) worked out on a copy of the state
    GetEndTurnPreview = () => {
        if (this.gameState.phase !== Phase.Action)
        {
            return null;
        }
        const copy = new GameState(clone(this.gameState.toData()));
        const player = copy.GetCurrentPlayerState();
        const discard = MilitaryCardsOverLimit(player);
        // (An empty deck is made again from the discard, except in Age IV)
        const drawable = copy.age === Age.Age4 ? 0 : copy.militaryDeck.length + copy.militaryDiscard.length;
        const militaryDraw = Math.min(Math.max(player.GetMilitaryActionsRemaining(), 0), 3, drawable);
        // What discarding the cards over the hand limit does (e.g. Eleanor of Aquitaine's culture), then cards that score at
        // the end of the turn (e.g. Genghis Khan), which happens before production, as in EndTurn
        if (discard > 0)
        {
            FireTriggers(player, TriggerEvents.DiscardMilitary, { count: discard });
        }
        FireTriggers(player, TriggerEvents.EndOfTurn);
        MoveDefinitions.NobelPrize(copy, player);
        const endOfTurn = [...copy.triggerLog];
        // Why such cards will not score this time (e.g. Genghis Khan when not among the 2 strongest)
        const endOfTurnNotes = [...copy.triggerNotes];
        // Saladin: his choice is made right after the action phase, with ending the turn (the current one preselected)
        const saladin = player.leader?.name === LeaderNames.Saladin ? { current: player.CardState(LeaderNames.Saladin).choice ?? null } : null;
        return { discard, militaryDraw, civilActionsLeft: player.GetCivilActionsRemaining(), endOfTurn, endOfTurnNotes, saladin,
            ...RunProductionPhase(player) };
    };

    notify = () => {
        this.save();
        this.updateGameView(this.GetCurrentViewModel());
    };

    static Validate = Rules.Validate;
    static Apply = Rules.Apply;
    static Replay = Rules.Replay;

    fail = (error) => {
        this.lastError = error;
        this.notify();
        return { ok: false, error };
    }

    succeed = () => {
        this.lastError = null;
        this.notify();
        return { ok: true };
    }

    // A move that throws (an engine bug) is reported like an illegal move, leaving the state as it was
    // Adds the player's payment choices to a move
    withPoolChoice = (move) => ({
        ...move,
        // Paying part in another currency (Trade Routes Agreement), when the player has chosen to
        ...(this.useTradeRoute ? { tradeRoute: true } : {}),
        // Not using a civil action for some actions only (Gutenberg's), when the player has chosen not to
        ...(this.declinedRestricted ? { skipRestricted: true } : {}),
    });

    // Using (or not) a civil action for some actions only (Gutenberg's) on the next moves
    setUseRestricted = (use) => {
        this.declinedRestricted = !use;
        this.notify();
    };

    // Trade Routes Agreement: whether to pay part of the next costs in the other currency (off by default)
    setUseTradeRoute = (useTradeRoute) => {
        this.useTradeRoute = useTradeRoute;
        this.notify();
    };


    submitMove = (move) => {
        // One at a time: a move made while the last is still on its way to the server would be sent again with it
        if (this.waiting)
        {
            return this.fail("Still sending your last move");
        }
        try {
            return this.applyMove(this.withPoolChoice(move));
        } catch (e) {
            console.error(e);
            return this.fail(`Something went wrong with that move: ${e.message}`);
        }
    }

    applyMove = (move) => {
        const error = GameEngine.Validate(this.gameState, move);
        if (error !== null)
        {
            return this.fail(error);
        }
        // A decision: worked out here while it only leads to more choices for the same player (e.g. Development of
        // Planning's pick, its follow-up, going back), so nothing is sent until they have finished; then the whole chain
        // goes to the authority at once. Sent straight away when it would reveal something hidden (a card drawn).
        if (move.type === MoveTypes.Decide)
        {
            let step;
            try {
                step = Rules.Step(this.gameState, move);
            } catch (e) {
                return this.commit(move);
            }
            const known = new Set(this.gameState.decisions.map(d => d.id));
            const more = step.after.decisions.some(d => d.player === move.player && !known.has(d.id));
            // Also kept here, like any move of the turn (so it can be undone): the current player's own choice on their turn
            // that concerns no one else (e.g. Boudica's food, resource or science)
            const ownTurnChoice = move.player === this.gameState.currentPlayer && OnlyTheirs(this.gameState, step.after, move.player);
            // (or that shows a card only the server knows: Ian Fleming's look)
            const reveals = require('./Moves/Decisions').DecisionTypes[this.gameState.decisions.find(d => d.id === move.decisionId)?.type]?.reveals;
            if ((!more && !ownTurnChoice) || reveals || RevealsHidden(this.gameState, step.after))
            {
                return this.commit(move);
            }
            this.gameState = step.after;
            this.pendingMoves.push(move);
            this.pendingDescriptions.push(step.description);
            return this.succeed();
        }
        // A move that cannot be undone (it reveals something) is the authority's to apply, with the turn so far; as is
        // any move outside one's own turn
        if (Rules.IsIrreversible(move, this.gameState) || move.player !== this.gameState.currentPlayer)
        {
            return this.commit(move);
        }
        const { after, description } = Rules.Step(this.gameState, move);
        this.gameState = after;
        this.pendingMoves.push(move);
        this.pendingDescriptions.push(description);
        return this.succeed();
    }

    // Sends the player's moves so far (their turn, or a chain of decisions worked out here) plus this one to the
    // authority, then carries on from its new view. Other players' pending moves (on a shared screen) stay on top of it.
    commit = (move) => {
        const entries = this.pendingMoves.map((pending, index) => ({ move: pending, description: this.pendingDescriptions[index] }));
        const mine = entries.filter(entry => entry.move.player === move.player);
        const others = entries.filter(entry => entry.move.player !== move.player);
        const result = this.authority.Commit(move.player, [...mine.map(entry => entry.move), move]);
        const done = (answer) => {
            this.waiting = false;
            if (!answer.ok)
            {
                return this.fail(answer.error);
            }
            const view = answer.view ?? this.authority.ViewFor(this.viewer);
            if (others.length === 0)
            {
                this.loadView(view);
            } else {
                this.pendingMoves = others.map(entry => entry.move);
                this.pendingDescriptions = others.map(entry => entry.description);
                this.rebase(view);
            }
            return this.succeed();
        };
        if (typeof result?.then === 'function')
        {
            this.waiting = true;
            this.notify();
            return result.then(done, (e) => { this.waiting = false; return this.fail(`Could not reach the server: ${e.message}`); });
        }
        return done(result);
    };

    // Replaces the pending moves, keeping the old ones if the new sequence is not legal
    setPendingMoves = (moves, descriptions) => {
        try {
            this.gameState = GameEngine.Replay(this.checkpointData, moves);
        } catch (e) {
            return this.fail(e.message);
        }
        this.pendingMoves = moves;
        this.pendingDescriptions = descriptions;
        return this.succeed();
    }

    undo = () => {
        if (this.pendingMoves.length === 0)
        {
            return this.fail("Nothing to undo");
        }
        return this.setPendingMoves(this.pendingMoves.slice(0, -1), this.pendingDescriptions.slice(0, -1));
    }

    // Back to the start of the action phase (just after passing the political phase, when that can still be undone),
    // or with toPolitical, back to the political phase: the start of the turn, or just after the last irreversible move
    // (e.g. Caesar's first political action, when it revealed something). See GetResets for when each is possible.
    resetTurn = (toPolitical = false) => {
        const keep = toPolitical ? 0 : this.ActionPhaseStart();
        return this.setPendingMoves(this.pendingMoves.slice(0, keep), this.pendingDescriptions.slice(0, keep));
    };

    // How many pending moves come before the action phase: up to and including the pass of the political phase (none
    // when it is not pending: the turn can only go back to where it can be undone from)
    ActionPhaseStart = () => this.pendingMoves.findIndex(move => move.type === MoveTypes.PassPoliticalPhase) + 1;

    // What resetting can do now: the action phase (moves taken in it), and the political phase (once passed, while
    // that can still be undone)
    GetResets = () => ({
        actionPhase: this.pendingMoves.length > this.ActionPhaseStart(),
        politicalPhase: this.ActionPhaseStart() > 0,
    });

    // Removes a single earlier move, keeping everything after it if it is still legal
    retractMove = (index) => {
        if (index < 0 || index >= this.pendingMoves.length || !this.GetRetractable().pendingMoves[index].canUndo)
        {
            return this.fail("That move can no longer be undone: later moves depend on it");
        }
        return this.setPendingMoves(
            this.pendingMoves.filter((_, i) => i !== index),
            this.pendingDescriptions.filter((_, i) => i !== index));
    }

    // International Agreement declined, even after cards were taken (worked out here): they go back, and it is declined
    declineAgreement = (player) => {
        if (this.pendingMoves.some(move => move.type === MoveTypes.Decide && move.player === player))
        {
            this.retractDecisions(player, true);
        }
        const decision = NextDecisionFor(this.gameState, player);
        if (decision?.type !== 'agreementTake')
        {
            return this.fail("There is no agreement to decline");
        }
        return this.submitMove(this.TryDecision(decision.id, 'decline').move);
    };

    // Takes back a player's decisions worked out here and not yet sent (see applyMove): the last one, or all of them (back
    // to the first decision of the chain, e.g. International Agreement's accept or decline)
    retractDecisions = (player, all = false) => {
        const indexes = this.pendingMoves.map((move, index) => ({ move, index }))
            .filter(entry => entry.move.type === MoveTypes.Decide && entry.move.player === player).map(entry => entry.index);
        if (indexes.length === 0)
        {
            return this.fail("Nothing to take back");
        }
        const remove = new Set(all ? indexes : [indexes.at(-1)]);
        return this.setPendingMoves(this.pendingMoves.filter((_, i) => !remove.has(i)), this.pendingDescriptions.filter((_, i) => !remove.has(i)));
    };

    // Puts a card taken this turn back into the card row
    retractCardTake = (cardId) => {
        // Taken for International Agreement: the card goes back, and the cards taken after it are taken again (those that
        // still fit)
        const agreementIndex = this.pendingMoves.findIndex(move => IsAgreementTake(move) && move.value.cardId === cardId);
        if (agreementIndex !== -1)
        {
            const player = this.pendingMoves[agreementIndex].player;
            const later = this.pendingMoves.slice(agreementIndex + 1).filter(IsAgreementTake).map(move => move.value.cardId);
            const result = this.setPendingMoves(this.pendingMoves.slice(0, agreementIndex), this.pendingDescriptions.slice(0, agreementIndex));
            if (!result.ok)
            {
                return result;
            }
            later.forEach(laterCard => {
                const decision = NextDecisionFor(this.gameState, player);
                const tried = decision?.type === 'agreementTake' ? this.TryDecision(decision.id, { cardId: laterCard }) : null;
                if (tried?.ok)
                {
                    this.applyMove(tried.move);
                }
            });
            return this.succeed();
        }
        const index = this.pendingMoves.findIndex(move => move.type === MoveTypes.TakeCard && move.cardId === cardId);
        if (index === -1)
        {
            return this.fail("That card was not taken during this turn");
        }
        return this.retractMove(index);
    }
}

module.exports = GameEngine
// In the dev server: a game already loaded in the page keeps the objects built by the old engine code, so an
// engine change could otherwise show stale behaviour (e.g. cards missing new text or pictures). Reload instead.
if (typeof module !== 'undefined' && module.hot)
{
    module.hot.dispose(() => window.location.reload());
}
